import type { ReactNode } from 'react';
import { formatNumber, formatTaka, pluralize } from '@/utils/number';
import type {
  PcmpAccountsAmount,
  PcmpAccountsPaymentTypeTotal,
} from '../../types/pcmpAccounts.types';

/** One fact tile of a batch page. */
export function PcmpBatchFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border bg-card px-4 py-3 shadow-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/** A bucket's amount, with its proposal count below. */
export function PcmpBatchAmountFact({
  label,
  value,
}: {
  label: string;
  value: PcmpAccountsAmount;
}) {
  return (
    <PcmpBatchFact label={label}>
      <span className="text-lg font-semibold tabular-nums">{formatTaka(value.amount)}</span>
      <span className="text-xs text-muted-foreground tabular-nums">
        {formatNumber(value.count)} {pluralize(value.count, 'proposal')}
      </span>
    </PcmpBatchFact>
  );
}

/** How the whole batch goes out, by payment type (legacy LDS-695). */
export function PcmpPaymentTypeStrip({ totals }: { totals: PcmpAccountsPaymentTypeTotal[] }) {
  if (totals.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">By payment type:</span>
      {totals.map((total) => (
        <span
          key={total.paymentType}
          className="rounded-full border bg-card px-3 py-1 whitespace-nowrap tabular-nums"
        >
          <span className="font-medium">{total.paymentTypeLabel}</span> {formatTaka(total.amount)}
          <span className="text-muted-foreground"> · {formatNumber(total.count)}</span>
        </span>
      ))}
    </div>
  );
}
